import { useEffect, useState } from 'react';
import { useAuth } from '@toki/auth';
import type { Cliente, Requester } from '../services/api';
import { createRequester, listClientes, updateRequester } from '../services/api';
import styles from '../screens/Mantenimiento.module.css';

/**
 * El formulario ÚNICO de una persona de cliente (paso 85a).
 *
 * Lo abren tres sitios —la ficha de un cliente, la pantalla Usuarios y el
 * «Crear cuenta» del alta de un ticket— y los tres llegan al MISMO endpoint
 * (`POST /admin/users/requesters`). Dos altas con reglas propias es K38: la que
 * se queda atrás es la que deja crear personas sin cliente.
 *
 * - Desde un cliente, el cliente viene fijado (`fixedCliente`) y no se elige.
 * - El cliente es obligatorio en el alta; en la edición también, salvo que la
 *   persona sea anterior al 85a y todavía no tenga, en cuyo caso se le puede
 *   poner (y sus tickets sin cliente pasan a él).
 * - Solo se ofrecen clientes ACTIVOS: dar de alta a alguien en un cliente
 *   desactivado es crear una cuenta que nace sin poder entrar (el backend lo
 *   rechaza igual, 409 `CLIENTE_INACTIVE`).
 */
export function PersonaModal({
  persona,
  initialEmail = '',
  initialName = '',
  fixedCliente,
  onClose,
  onSaved,
}: {
  persona?: Requester;
  initialEmail?: string;
  initialName?: string;
  fixedCliente?: { id: string; nombre: string };
  onClose: () => void;
  onSaved: (p: Requester, info: { accessSent: boolean }) => void;
}) {
  const { provider } = useAuth();
  const editing = !!persona;
  const [email, setEmail] = useState(persona?.email ?? initialEmail);
  const [nombre, setNombre] = useState(persona?.displayName ?? initialName);
  const [clienteId, setClienteId] = useState(fixedCliente?.id ?? persona?.clienteId ?? '');
  const [sendAccess, setSendAccess] = useState(true);
  const [clientes, setClientes] = useState<Cliente[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (fixedCliente) return;
    let cancelado = false;
    (async () => {
      const token = await provider.getAccessToken();
      if (!token) return;
      try {
        const cs = await listClientes(token);
        if (!cancelado) setClientes(cs);
      } catch (e: any) {
        if (!cancelado) setError(e.message);
      }
    })();
    return () => { cancelado = true; };
  }, [provider, fixedCliente]);

  // El cliente actual se ofrece aunque esté desactivado: no se puede mover a
  // nadie A un cliente desactivado, pero dejarlo donde está no es moverlo.
  const opciones = (clientes ?? []).filter((c) => c.isActive || c.id === persona?.clienteId);

  const save = async () => {
    const token = await provider.getAccessToken();
    if (!token) return;
    setSaving(true);
    setError(null);
    try {
      if (editing && persona) {
        const changes: { email?: string; displayName?: string; clienteId?: string } = {};
        if (email.trim() !== persona.email) changes.email = email.trim();
        if (nombre.trim() !== (persona.displayName ?? '')) changes.displayName = nombre.trim();
        if (clienteId && clienteId !== persona.clienteId) changes.clienteId = clienteId;
        const p = await updateRequester(persona.id, changes, token);
        onSaved(p, { accessSent: false });
      } else {
        const p = await createRequester(
          { email: email.trim(), displayName: nombre.trim(), clienteId, sendAccess },
          token,
        );
        onSaved(p, { accessSent: sendAccess });
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const valid = email.trim() !== '' && nombre.trim() !== '' && clienteId !== '';

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()} role="dialog" aria-label={editing ? 'Editar persona' : 'Nueva persona'}>
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>{editing ? 'Editar persona' : 'Nueva persona'}</h3>
          <button type="button" className={styles.modalClose} onClick={onClose} aria-label="Cerrar">&times;</button>
        </div>

        {error && <div className={styles.error}>{error}</div>}

        <div className={styles.modalBody}>
          <div className={styles.fieldGroup}>
            <label className={styles.label} htmlFor="persona-email">Email</label>
            <input
              id="persona-email"
              className={styles.input}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="nombre@empresa.com"
            />
          </div>
          <div className={styles.fieldGroup}>
            <label className={styles.label} htmlFor="persona-nombre">Nombre</label>
            <input id="persona-nombre" className={styles.input} value={nombre} onChange={(e) => setNombre(e.target.value)} />
          </div>
          <div className={styles.fieldGroup}>
            <label className={styles.label} htmlFor="persona-cliente">Cliente</label>
            {fixedCliente ? (
              <input id="persona-cliente" className={styles.input} value={fixedCliente.nombre} disabled />
            ) : (
              <select
                id="persona-cliente"
                className={styles.select}
                value={clienteId}
                onChange={(e) => setClienteId(e.target.value)}
                disabled={clientes === null}
              >
                <option value="">{clientes === null ? 'Cargando…' : 'Elige un cliente…'}</option>
                {opciones.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nombre}{c.isActive ? '' : ' (desactivado)'}
                  </option>
                ))}
              </select>
            )}
            {editing && persona && !persona.clienteId && (
              <span className={styles.muted}>
                Sin cliente: es una cuenta anterior a los clientes. Al ponérselo, sus tickets sin
                cliente pasan a él.
              </span>
            )}
            {clientes !== null && opciones.length === 0 && (
              <span className={styles.muted}>No hay ningún cliente activo. Un administrador tiene que crearlo.</span>
            )}
          </div>

          {!editing && (
            <label className={styles.checkRow}>
              <input type="checkbox" checked={sendAccess} onChange={(e) => setSendAccess(e.target.checked)} />
              <span>
                Enviarle ahora el acceso al portal. Desmárcalo para cargar la lista de un cliente y
                avisarles después. <strong>Aun sin acceso, sus correos a soporte ya abren ticket.</strong>
              </span>
            </label>
          )}
        </div>

        <div className={styles.modalFooter}>
          <button className={styles.cancelBtn} onClick={onClose}>Cancelar</button>
          <button className={styles.saveBtn} onClick={save} disabled={saving || !valid}>
            {saving ? 'Guardando…' : editing ? 'Guardar' : sendAccess ? 'Crear y enviar acceso' : 'Crear'}
          </button>
        </div>
      </div>
    </div>
  );
}
